import { ConstructionStep, EngineEvent, EventCallbackData } from '../types';
import { AnimationEngine } from './AnimationEngine';
import { SpeechManager } from '../utils/SpeechManager';

export class Timeline {
  private steps: ConstructionStep[] = [];
  private stepStartTimes: number[] = [];
  private totalDurationMs: number = 0;
  private currentStepIndex: number = -1;

  private animationEngine: AnimationEngine;
  private listeners: Map<EngineEvent, Set<(data: EventCallbackData) => void>> = new Map();

  constructor(animationEngine: AnimationEngine) {
    this.animationEngine = animationEngine;
    this.animationEngine.onTick(this.handleTick);
  }

  public setSteps(steps: ConstructionStep[]): void {
    const speechManager = SpeechManager.getInstance();
    this.steps = steps;
    this.stepStartTimes = [];
    let cumulativeTime = 0;

    for (const step of steps) {
      this.stepStartTimes.push(cumulativeTime);
      
      if (step.narration) {
        const estSpeechMs = speechManager.estimateDurationMs(step.narration);
        step.duration = Math.max(step.duration, estSpeechMs);
      }
      
      cumulativeTime += step.duration;
    }

    this.totalDurationMs = cumulativeTime;
    // Reset to -1 so seeking to 0 triggers step 0 narration emit
    this.currentStepIndex = -1;
    // Reset speech hold state
    this._holdingForSpeech = false;
    this.updateStateForTime(0);
  }

  public getSteps(): ConstructionStep[] {
    return this.steps;
  }

  public getStepStartTimes(): number[] {
    return this.stepStartTimes;
  }

  public getTotalDuration(): number {
    return this.totalDurationMs;
  }

  public getCurrentStepIndex(): number {
    return this.currentStepIndex;
  }

  public play(): void {
    if (this.totalDurationMs === 0) return;
    if (this.animationEngine.getCurrentTime() >= this.totalDurationMs) {
      this.seek(0);
    }
    this._holdingForSpeech = false;
    this.animationEngine.start();
    this.emit('play');

    // Always trigger step narration when playback starts from step 0 or any step
    if (this.currentStepIndex >= 0 && this.steps[this.currentStepIndex]?.narration) {
      this.emit('narration');
    }
  }

  public pause(): void {
    this.animationEngine.pause();
    this.emit('pause');
  }

  public restart(): void {
    this.seek(0);
    this.play();
  }

  public seek(timeMs: number): void {
    const clampedTime = Math.max(0, Math.min(this.totalDurationMs, timeMs));
    // Use setSilentTime to avoid re-entrantly calling handleTick
    this.animationEngine.setSilentTime(clampedTime);
    this.updateStateForTime(clampedTime);
  }

  public gotoStep(stepIndex: number): void {
    if (stepIndex < 0 || stepIndex >= this.steps.length) return;
    // Stop any current speech and reset hold state
    SpeechManager.getInstance().stop();
    this._holdingForSpeech = false;
    const startTime = this.stepStartTimes[stepIndex];
    const clampedTime = Math.max(0, Math.min(this.totalDurationMs, startTime));
    this.animationEngine.pause();
    // Use setSilentTime so handleTick is NOT triggered during repositioning
    this.animationEngine.setSilentTime(clampedTime);
    // Force currentStepIndex to -1 so updateStateForTime always sees a step change
    this.currentStepIndex = -1;
    this.updateStateForTime(clampedTime);
    // Restart playback from this step
    this.animationEngine.start();
    this.emit('play');
  }

  public nextStep(): void {
    if (this.currentStepIndex < this.steps.length - 1) {
      this.gotoStep(this.currentStepIndex + 1);
    }
  }

  public prevStep(): void {
    if (this.currentStepIndex > 0) {
      this.gotoStep(this.currentStepIndex - 1);
    }
  }

  public on(event: EngineEvent, callback: (data: EventCallbackData) => void): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  private emit(event: EngineEvent): void {
    const set = this.listeners.get(event);
    if (!set) return;

    const data: EventCallbackData = {
      stepIndex: Math.max(0, this.currentStepIndex),
      totalSteps: this.steps.length,
      progress: this.totalDurationMs > 0 ? this.animationEngine.getCurrentTime() / this.totalDurationMs : 0,
      time: this.animationEngine.getCurrentTime(),
      stepTitle: this.steps[this.currentStepIndex]?.title,
      narration: this.steps[this.currentStepIndex]?.narration,
    };

    set.forEach((cb) => cb(data));
  }

  // Whether we are currently holding playback position to let speech finish
  private _holdingForSpeech: boolean = false;

  private handleTick = (currentTimeMs: number, _deltaMs: number): void => {
    if (currentTimeMs >= this.totalDurationMs) {
      if (SpeechManager.getInstance().isSpeaking()) {
        this.animationEngine.setSilentTime(Math.max(0, this.totalDurationMs - 1));
        this.animationEngine.pause();
        this._holdingForSpeech = true;
        SpeechManager.getInstance().onEnd(() => {
          if (this._holdingForSpeech) {
            this._holdingForSpeech = false;
            this.updateStateForTime(this.totalDurationMs);
            this.emit('complete');
          }
        });
        return;
      }
      this.updateStateForTime(this.totalDurationMs);
      this.animationEngine.pause();
      this._holdingForSpeech = false;
      this.emit('complete');
      return;
    }

    const curIdx = this.currentStepIndex;
    if (curIdx >= 0 && curIdx < this.steps.length - 1) {
      const nextStepStartTime = this.stepStartTimes[curIdx + 1];

      if (currentTimeMs >= nextStepStartTime) {
        if (SpeechManager.getInstance().isSpeaking()) {
          // Clamp time right before boundary to prevent step change & narration cut-off
          this.animationEngine.setSilentTime(nextStepStartTime - 1);
          this.animationEngine.pause();
          this._holdingForSpeech = true;
          SpeechManager.getInstance().onEnd(() => {
            if (this._holdingForSpeech) {
              this._holdingForSpeech = false;
              this.animationEngine.start();
            }
          });
          return;
        }
      }
    }

    this.updateStateForTime(currentTimeMs);
    this.emit('progress');
  };

  private updateStateForTime(timeMs: number): void {
    let newStepIndex = 0;
    for (let i = 0; i < this.stepStartTimes.length; i++) {
      if (timeMs >= this.stepStartTimes[i]) {
        newStepIndex = i;
      } else {
        break;
      }
    }

    const stepIndexChanged = newStepIndex !== this.currentStepIndex;
    this.currentStepIndex = newStepIndex;

    for (let i = 0; i < this.steps.length; i++) {
      const step = this.steps[i];
      const stepStart = this.stepStartTimes[i];
      const stepEnd = stepStart + step.duration;

      for (const anim of step.animations) {
        if (timeMs < stepStart) {
          anim.evaluate(0);
        } else if (timeMs >= stepEnd) {
          anim.evaluate(1);
        } else {
          const localTime = timeMs - stepStart;
          const progress = localTime / step.duration;
          anim.evaluate(progress);
        }
      }
    }

    if (stepIndexChanged) {
      this.emit('step');
      if (this.steps[newStepIndex]?.narration) {
        this.emit('narration');
      }
    }
  }
}
